import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAppSelector } from "app/hooks";
import { selectCurrentPractice } from "features/auth/auth-slice";
import useUserRoles from "hooks/use-user-roles.hook";
import { isPathAllowedForReportsOnly } from "utils/auth-access.utils";

type ReportsOnlyGuardProps = {
  children: ReactNode;
};

/**
 * Confines a "Financial Reports Only Access" user (User.financial_reports_only_access — the
 * accountant/bookkeeper restriction set on Administration → Users → General Access) to the
 * Reports section plus their own Settings. One wrapper around the authenticated Layout covers
 * every child route, deep links included, instead of a `!isReportsOnlyUser` condition on each
 * route registration.
 *
 * QuickBooks is the one conditional addition: a restricted user holding the "Access to
 * QuickBooks" grant (canAccessQBO) in a practice with QBO enabled may open `/quickbooks` too —
 * the same pair the side menu checks, so the guard never redirects off a link the menu offers.
 *
 * Fail-open while the user is still the pre-auth placeholder: `isReportsOnlyUser` is false until
 * /auth/users/me/ resolves, so nothing redirects prematurely and the URL is never rewritten for
 * an unrestricted user on a hard refresh (the ENG-1668 class of bug).
 */
export default function ReportsOnlyGuard({ children }: ReportsOnlyGuardProps) {
  const location = useLocation();
  const { isReportsOnlyUser, canAccessQBO } = useUserRoles();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const canAccessQuickbooks = Boolean(
    canAccessQBO && currentPractice?.is_quickbooks_enabled
  );

  if (
    isReportsOnlyUser &&
    !isPathAllowedForReportsOnly(location.pathname, { canAccessQuickbooks })
  ) {
    return <Navigate to="/reports" replace />;
  }

  return <>{children}</>;
}
